import { useState, useEffect } from 'react';
import { adoptableCats } from '../mocks/adoptableCats';

export interface AdoptableCat {
  id: string;
  name: string;
  breed: string;
  age: string;
  gender: string;
  size: string;
  description: string;
  image: string;
  shelterId: string;
  shelterName: string;
  distance: number;
  photos: string[];
  attributes: {
    spayedNeutered: boolean;
    houseTrained: boolean;
    declawed: boolean;
    specialNeeds: boolean;
  };
  environment: {
    children: boolean;
    dogs: boolean;
    cats: boolean;
  };
  contact: {
    email: string;
    phone: string;
    address: {
      city: string;
      state: string;
      postcode: string;
      country: string;
    };
  };
}

export interface Shelter {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  phone: string;
  email: string;
  distance: number;
  catsAvailable: number;
  hours?: string;
  latitude?: number;
  longitude?: number;
}

interface UseNearbyAdoptionsResult {
  cats: AdoptableCat[];
  shelters: Shelter[];
  totalCats: number;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
}

// Map mock data to AdoptableCat interface
function mapMockCats(radius: number): AdoptableCat[] {
  return adoptableCats
    .filter((cat) => {
      const dist = parseFloat(String(cat.distance));
      return dist <= radius;
    })
    .map((cat) => {
      const [cityRaw, stateRaw] = (cat.shelterCity || '').split(', ');
      return {
        id: String(cat.id),
        name: cat.name,
        breed: cat.breed,
        age: mapAge(cat.age),
        gender: cat.gender,
        size: 'Medium',
        description: cat.personality,
        image: cat.image,
        shelterId: String(cat.id),
        shelterName: cat.shelter,
        distance: parseFloat(String(cat.distance)),
        photos: [cat.image],
        attributes: {
          spayedNeutered: cat.neutered,
          houseTrained: true,
          declawed: false,
          specialNeeds: false,
        },
        environment: {
          children: cat.personality.toLowerCase().includes('kid') || cat.personality.toLowerCase().includes('child'),
          dogs: cat.personality.toLowerCase().includes('dog'),
          cats: true,
        },
        contact: {
          email: '',
          phone: '',
          address: {
            city: cityRaw || 'Portland',
            state: stateRaw || 'OR',
            postcode: '',
            country: 'US',
          },
        },
      };
    });
}

function mapAge(age: string): string {
  const lower = age.toLowerCase();
  // Baby/Kitten: 3 months or less
  if (lower.includes('3 month') || lower.includes('2 month') || lower.includes('1 month')) return 'Baby';
  // Young: under 2 years
  if (lower.includes('month') || lower.includes('1 year') || lower.includes('1.5')) return 'Young';
  // Senior: 7+ years
  if (lower.includes('9 year') || lower.includes('8 year') || lower.includes('7 year') || lower.includes('senior')) return 'Senior';
  // Adult: everything else
  return 'Adult';
}

function buildShelters(cats: AdoptableCat[]): Shelter[] {
  const shelterMap = new Map<string, Shelter>();
  cats.forEach((cat) => {
    if (!shelterMap.has(cat.shelterName)) {
      shelterMap.set(cat.shelterName, {
        id: cat.shelterId,
        name: cat.shelterName,
        address: '',
        city: cat.contact.address.city,
        state: cat.contact.address.state,
        postcode: '',
        phone: '',
        email: '',
        distance: cat.distance,
        catsAvailable: 0,
      });
    }
    const s = shelterMap.get(cat.shelterName)!;
    s.catsAvailable += 1;
  });
  return Array.from(shelterMap.values()).sort((a, b) => a.distance - b.distance);
}

export function useNearbyAdoptions(
  latitude: number | null,
  longitude: number | null,
  radius: number = 25
): UseNearbyAdoptionsResult {
  const [cats, setCats] = useState<AdoptableCat[]>([]);
  const [shelters, setShelters] = useState<Shelter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = () => {
    setIsLoading(true);
    setTimeout(() => {
      const mapped = mapMockCats(radius);
      setCats(mapped);
      setShelters(buildShelters(mapped));
      setLastUpdated(new Date());
      setIsLoading(false);
    }, 600);
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, radius]);

  return {
    cats,
    shelters,
    totalCats: cats.length,
    isLoading,
    error: null,
    lastUpdated,
    refetch: load,
  };
}
